import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { RotateCw, ChevronLeft } from 'lucide-react';
import { ExerciseBanner } from './ExerciseBanner';
import { resolveFontSize } from '../config/fontSizes';

/**
 * ResultsScreen для ImageChoice
 */
const ImageChoiceResultsScreen = ({ 
  correct = 0,
  incorrect = 0,
  onRestart,
  onRestartCorrect,
  onRestartIncorrect,
  onBack,
  topic,
  exercise
}) => {
  const total = correct + incorrect;
  const percentage = total > 0 ? (correct / total) * 100 : 0;
  const { t } = useLanguage();

  return (
    <div className="results-page">
      <div className="results-container">
        <div className="results-header">
          <h1 className="results-title">🎉 {t('exerciseComplete')}!</h1>
          <p className="results-subtitle">{topic?.germanTitle} - {exercise?.title}</p>
        </div>

        <div className="results-stats-grid">
          <div className="results-stat-card correct">
            <div className="results-stat-number">{correct}</div>
            <div className="results-stat-label">{t('answersCorrect')}</div>
            <div className="results-stat-pct">{total > 0 ? (correct / total * 100).toFixed(0) : 0}% {t('ofAll')}</div>
            {correct > 0 && (
              <button onClick={onRestartCorrect} className="results-stat-btn">
                <RotateCw size={16} /> {t('repeatCorrect')}
              </button>
            )}
          </div>
          <div className="results-stat-card incorrect">
            <div className="results-stat-number">{incorrect}</div>
            <div className="results-stat-label">{t('errors')}</div>
            <div className="results-stat-pct">{total > 0 ? (incorrect / total * 100).toFixed(0) : 0}% {t('ofAll')}</div>
            {incorrect > 0 && (
              <button onClick={onRestartIncorrect} className="results-stat-btn">
                <RotateCw size={16} /> {t('repeatIncorrect')}
              </button>
            )}
          </div>
        </div>

        <div className="results-progress-section">
          <div className="results-progress-header">
            <span className="results-progress-label">{t('progress')}:</span>
            <span className="results-progress-value">{Math.round(percentage)}%</span>
          </div>
          <div className="results-progress-track">
            <div className="results-progress-fill" style={{ width: `${percentage}%` }} />
          </div>
        </div>

        <div className="results-detail-card">
          <h3 className="results-detail-title">📊 {t('total')}:</h3>
          <div className="results-detail-row">
            <span className="results-detail-label">{t('totalQuestions')}:</span>
            <span className="results-detail-value">{total}</span>
          </div>
          <div className="results-detail-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 12 }}>
            <span className="results-detail-label">{t('answersCorrect')}:</span>
            <span className="results-detail-value" style={{ color: 'var(--mint)' }}>{correct}</span>
          </div>
          <div className="results-detail-row">
            <span className="results-detail-label">{t('errors')}:</span>
            <span className="results-detail-value" style={{ color: 'var(--coral)' }}>{incorrect}</span>
          </div>
        </div>

        <div className="results-actions">
          <button onClick={onRestart} className="results-btn-primary">
            <RotateCw size={20} /> {t('repeatAll')}
          </button>
          <button onClick={onBack} className="results-btn-secondary">
            🚪 {t('exit')}
          </button>
        </div>

        <div className="results-tip">
          💡 <strong>{t('tip')}:</strong> {t('reviewMistakes')}!
        </div>
      </div>
    </div>
  );
};

export const ImageChoiceExercise = ({ 
  exercise, 
  onAnswerSubmit, 
  onBack, 
  onExit,
  topic,
  showResultsScreen = true,
  totalCorrect = 0,
  totalIncorrect = 0,
  currentExerciseIndex = 0,
  totalExercises = 1
}) => {
  const { t } = useLanguage();
  // uses shared resolveFontSize from config
  const [selectedOption, setSelectedOption] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [incorrectAnswers, setIncorrectAnswers] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [filteredMode, setFilteredMode] = useState(null);

  const content = exercise.content || {};
  // Normalize options (support string or object) and preserve editor order
  let options = content.options || [];
  options = options.map(o => (typeof o === 'string' ? { text: o } : (o || { text: '' })));
  if (options.length > 0 && options[0].order !== undefined) {
    options = [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  // Compute correct answer index robustly: prefer stored correctAnswerText, fallback to numeric index
  const storedCorrectText = (content.correctAnswerText || content.correctAnswer || exercise.correctAnswer || '').toString().trim().toLowerCase();
  let correctAnswerIndex = Number.isInteger(content.correctAnswerIndex) ? content.correctAnswerIndex : (Number.isInteger(exercise.content?.correctAnswerIndex) ? exercise.content.correctAnswerIndex : (Number.isInteger(exercise.correctAnswerIndex) ? exercise.correctAnswerIndex : 0));
  if (storedCorrectText) {
    const found = options.findIndex(o => (o.text || '').toString().trim().toLowerCase() === storedCorrectText);
    if (found !== -1) correctAnswerIndex = found;
  }
  const questionImage = content.questionImage;

  const activeIndices = filteredMode === 'correct' 
    ? correctAnswers
    : filteredMode === 'incorrect'
    ? incorrectAnswers
    : Array.from({ length: options.length }, (_, i) => i);

  const handleSubmit = () => {
    if (selectedOption === null) { alert(t('selectAnswer')); return; }
    const isCorrect = selectedOption === correctAnswerIndex;
    setFeedback({
      isCorrect,
      message: isCorrect ? t('correct') : t('incorrect'),
      correctAnswer: options[correctAnswerIndex]?.text,
    });
    setSubmitted(true);
    const newCorrectAnswers = isCorrect ? [...correctAnswers, 0] : correctAnswers;
    const newIncorrectAnswers = !isCorrect ? [...incorrectAnswers, 0] : incorrectAnswers;

    setTimeout(() => {
      if (onAnswerSubmit) {
        onAnswerSubmit({
          exerciseId: exercise.id,
          selectedOption,
          isCorrect,
          points: isCorrect ? (exercise.points || 10) : 0,
        });
      }
      if (showResultsScreen === false) {
        onBack();
      } else {
        setCorrectAnswers(newCorrectAnswers);
        setIncorrectAnswers(newIncorrectAnswers);
        setIsFinished(true);
      }
    }, 1500);
  };

  if (isFinished && showResultsScreen) {
    return (
      <ImageChoiceResultsScreen
        correct={correctAnswers.length}
        incorrect={incorrectAnswers.length}
        onRestart={() => { setSelectedOption(null); setSubmitted(false); setFeedback(null); setCorrectAnswers([]); setIncorrectAnswers([]); setIsFinished(false); setFilteredMode(null); }}
        onRestartCorrect={() => { setSelectedOption(null); setSubmitted(false); setFeedback(null); setCorrectAnswers([]); setIncorrectAnswers([]); setIsFinished(false); setFilteredMode('correct'); }}
        onRestartIncorrect={() => { setSelectedOption(null); setSubmitted(false); setFeedback(null); setCorrectAnswers([]); setIncorrectAnswers([]); setIsFinished(false); setFilteredMode('incorrect'); }}
        onBack={onBack}
        topic={topic}
        exercise={exercise}
      />
    );
  }

  if (submitted && feedback) {
    return (
      <div className="exercise-feedback">
        <div className={`exercise-feedback-card ${feedback.isCorrect ? 'correct' : 'wrong'}`}>
          <p style={{ fontSize: 24, fontWeight: 800 }}>{feedback.message}</p>
          {!feedback.isCorrect && (
            <p style={{ fontSize: 14, marginTop: 8, opacity: 0.8 }}>{t('correctAnswer')}: {feedback.correctAnswer}</p>
          )}
        </div>
      </div>
    );
  }

  const progressPct = totalExercises > 0 ? ((currentExerciseIndex + 1) / totalExercises) * 100 : 0;

  return (
    <div className="exercise-page" style={{ padding: 24 }}>
      {/* Unified Banner with progress bar */}
      <div style={{ margin: '0 auto 32px', maxWidth: 1000 }}>
        <ExerciseBanner
          currentIndex={currentExerciseIndex}
          totalExercises={totalExercises}
          correct={totalCorrect}
          incorrect={totalIncorrect}
          onExit={onExit}
          showProgress={true}
        />
        {/* Progress bar */}
        <div className="exercise-progress-row" style={{ marginTop: 8 }}>
          <span className="exercise-progress-label">{t('lessonProgress')}:</span>
          <div className="exercise-progress-track">
            <div className="exercise-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="exercise-progress-text">{currentExerciseIndex + 1} / {totalExercises}</span>
        </div>
      </div>

      {/* Main content */}
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        {/* Question card */}
        <div className="ic-question-card">
          <p
            style={{
              fontSize: resolveFontSize(content.fontSize) || (typeof content.fontSize === 'number' ? `${content.fontSize}px` : '18px'),
              fontWeight: 700,
              marginBottom: 24,
              color: content.textColor || 'var(--white)',
              textAlign: 'center'
            }}
          >
            {content.question || t('question')}
          </p>
          {questionImage && (
            <div style={{ marginBottom: 24 }}>
              <img src={questionImage} alt="Question" style={{ width: '100%', maxHeight: 256, objectFit: 'contain', borderRadius: 8 }} />
            </div>
          )}
        </div>

        {/* Options */}
        <div className="ic-options">
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => setSelectedOption(index)}
              className={`ic-option ${selectedOption === index ? 'selected' : ''}`}
            >
              {option.image && (
                <img src={option.image} alt={option.text} style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8 }} />
              )}
              <div style={{ flex: 1, textAlign: 'left' }}>
                <p style={{ fontWeight: 700, color: 'var(--white)' }}>{option.text}</p>
                {option.description && (
                  <p style={{ fontSize: 13, color: 'rgba(245,244,240,0.4)', marginTop: 4 }}>{option.description}</p>
                )}
              </div>
              <div className={`ic-radio ${selectedOption === index ? 'active' : ''}`}>
                {selectedOption === index && <div className="ic-radio-dot" />}
              </div>
            </button>
          ))}
        </div>

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={selectedOption === null}
          className="ic-submit-btn"
        >
          {t('submitAnswer')}
        </button>
      </div>
    </div>
  );
};

export default ImageChoiceExercise;
